import { AccessTimeFilled, EditOutlined, Star } from '@mui/icons-material';
import {
	Box,
	CardContent,
	CardHeader,
	Chip,
	IconButton,
	Link as MuiLink,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { UserAvatarUploadButton } from 'components/ui/emblem/avatar-upload-button';
import { UserEmblemBanner } from 'components/ui/emblem/emblem-banner';
import { format } from 'date-fns';
import { DisplayTemporaryLocation, UserProfile } from 'middleware-types';
import { Permission } from 'utils/permissions';
import { useSession } from 'utils/session';
import { useSiteUser } from 'utils/useSiteUser';
import { zoneDateOnly } from 'utils/utils';
import { useWorkTypesQuery } from './hooks';

/**
 * Temporary Location Display
 *
 * @param {{location : DisplayTemporaryLocation}} props
 */
const TemporaryLocationDisplay = (props: { location: DisplayTemporaryLocation }) => (
	<Stack direction="row" spacing={1} alignItems="flex-start">
		<Stack pt={0.25}>
			<AccessTimeFilled fontSize="small" color="warning" />
		</Stack>
		<Box>
			{props.location.startDate ? 'Will be' : 'Currently'} in&nbsp;
			<Typography fontWeight="medium" display="inline">
				{props.location.displayLocation}
			</Typography>
			&nbsp;
			{props.location.startDate
				? `from ${format(zoneDateOnly(props.location.startDate), 'MM/dd/yyyy')} to ${format(
						zoneDateOnly(props.location.endDate),
						'MM/dd/yyyy'
				  )}`
				: `until ${format(zoneDateOnly(props.location.endDate), 'MM/dd/yyyy')}`}
		</Box>
	</Stack>
);

/**
 * ProfileHeaderRead - A read only view of the Profile Header
 *
 * @param {ProfileHeaderReadProps} { profile, onBeginEdit }
 */
export const ProfileHeaderRead = (props: {
	profile: UserProfile;
	onBeginEdit: (initialFocusedField?: string) => void;
}) => {
	const { user } = useSession();
	const { profile } = props;
	const { hasPermission } = useSiteUser();
	const { workTypes } = useWorkTypesQuery();

	const canEdit =
		props.profile.id == user.userId || hasPermission(Permission.SocialUser_Profile_U);

	return (
		<>
			<UserEmblemBanner id={props.profile.id} enableUpload={canEdit} />
			<CardHeader
				sx={{ height: 'inherit' }}
				avatar={<UserAvatarUploadButton id={profile.id} disabled={!canEdit} />}
				title={
					<Typography variant="h2">
						{profile.name.firstName}{' '}
						{profile.name.middleName && profile.name.middleName}{' '}
						{profile.name.lastName && profile.name.lastName}{' '}
						{profile.name.suffix && profile.name.suffix}
					</Typography>
				}
				subheader={
					<Stack spacing={0.5}>
						{profile.companyTitle ? (
							<Typography variant="subtitle1">{`${profile.companyTitle} at ${profile.companyName}`}</Typography>
						) : (
							canEdit && (
								<Typography variant="subtitle1">
									<MuiLink onClick={() => props.onBeginEdit('companyTitle')}>
										Add Current Title...
									</MuiLink>
								</Typography>
							)
						)}
						{profile.displayTemporaryLocation && (
							<Typography variant="subtitle2">
								<TemporaryLocationDisplay
									location={profile.displayTemporaryLocation}
								/>
							</Typography>
						)}
						<Typography variant="subtitle2">
							<Stack direction="row" alignItems="flex-start" spacing={1}>
								<Stack pt={0.25}>
									<Star color="primary" fontSize="small" />
								</Stack>
								<Box>
									Based in&nbsp;
									<Typography fontWeight="medium" display="inline">
										{profile.displayLocation}
									</Typography>
								</Box>
							</Stack>
						</Typography>
						{profile.workCodes.length > 0 ? (
							workTypes ? (
								<Stack direction="row" spacing={0.5} paddingTop={0.5}>
									{profile.workCodes.map((wc, i) => (
										<Chip key={i} label={workTypes?.get(wc)} size="small" />
									))}
								</Stack>
							) : (
								<Skeleton width={300} height={50} />
							)
						) : (
							canEdit && (
								<Typography variant="subtitle2" color="primary">
									<MuiLink onClick={() => props.onBeginEdit('workCodes')}>
										Add Work...
									</MuiLink>
								</Typography>
							)
						)}
					</Stack>
				}
				action={
					canEdit && (
						<IconButton edge="end" onClick={() => props.onBeginEdit()}>
							<EditOutlined />
						</IconButton>
					)
				}
			/>
			{profile.about && (
				<CardContent>
					<Box paddingTop={1}>
						<Typography variant="body1">{profile.about}</Typography>
					</Box>
				</CardContent>
			)}
		</>
	);
};
